<template>
  <div
    ref="baseInfoRef"
    class="candidate-base-info-container"
    :class="hasResumeView ? 'has-resume-view' : ''"
    v-if="show"
  >
    <template v-if="isCandidate && showUploadButton">
      <div class="resume-info-head">
        <el-button class="import-resume-btn" type="text" @click="handleImport">{{ $t(`${i18nPrefix}.label.stepName.UPLOAD_RESUME`) }}</el-button>
      </div>
    </template>
    <template v-if="hrMiniResumeSetting && hrMiniResumeSetting.isHrSwitchMiniResume && !isEditMode">
      <div class="switch-mini-resume">
        <el-button-group>
          <el-button
            plain
            size="small"
            class="switch-button"
            :class="{'active': !hrMiniResumeSetting.isHrMiniResume}"
            @click="handleHrSwitchMiniResumeChange(false)"
          >
            {{ $t(`${i18nPrefix}.label.switchFullResume`) }}
          </el-button>
          <el-button
            plain
            size="small"
            class="switch-button"
            :class="{'active': hrMiniResumeSetting.isHrMiniResume}"
            :style="!hrMiniResumeSetting.isHrMiniResume ? 'border-left-color: #0096cc;' : ''"
            @click="handleHrSwitchMiniResumeChange(true)"
          >
            {{ $t(`${i18nPrefix}.label.switchMiniResume`) }}
          </el-button>
        </el-button-group>
      </div>
    </template>
    <WrappedForm
      ref="form"
      class="candidate-base-info"
      label-width="200px"
      :model="stepData"
      :formLines="formLines"
      :validateOnRuleChange="false"
    >
      <template #expectedSalaryInfo>
        <span class="span-info">{{ $t(`${i18nPrefix}.btn.expectedSalaryInfo`) }}</span>
      </template>

      <template #uploadPhoto="{ lineName }">
        <div class="photo-uploader">
          <div class="photo-container" :class="!stepData[lineName] && 'is-empty'">
            <img class="photo" :src="getPhotoPath(stepData[lineName])"/>
            <div class="photo-selector">
              <div class="selector-btn" @click="handlePhotoSelectBtnClick">{{ $t(`${i18nPrefix}.btn.selectPhoto`) }}</div>
            </div>
          </div>
          <input ref="fileSelector" class="file-selector" type="file" :accept="acceptPhotoFileTypeList.join(',')" @change="handlePhotoSelectorChange"/>
        </div>
      </template>

      <template #chatTool>
        <div class="tool-list-container chat-tool">
          <div v-for="(chatTool, index) in stepData.chatTool" class="tool-list-item" :key="index">
            <el-select v-model="chatTool.chatValue" clearable class="tool-type" :placeholder="$t(`${i18nPrefix}.message.select`)">
              <el-option
                v-for="item in chatToolListInfo"
                :key="item.id"
                :label="item.i18nName"
                :value="item.id"
              >
              </el-option>
            </el-select>
            <el-input v-model="chatTool.chatToolInfo" class="tool-account"></el-input>
            <div class="delete-tool-btn" @click="cancelChat(chatTool, index)">
              <i class="btn-icon el-icon-close"></i>
            </div>
          </div>
          <el-button class="add-tool-btn" type="text" @click="addChatTool">{{ $t(`${i18nPrefix}.btn.addChatTool`) }}</el-button>
        </div>
      </template>

      <template #socialNetwork>
        <div class="tool-list-container social-network">
          <div v-for="(social, index) in stepData.socialNetwork" clearable class="tool-list-item" :key="index">
            <el-select v-model="social.socialValue" class="tool-type" :placeholder="$t(`${i18nPrefix}.message.select`)">
              <el-option
                v-for="item in socialNetworkListInfo"
                :key="item.id"
                :label="item.i18nName"
                :value="item.id"
              >
              </el-option>
            </el-select>
            <el-input v-model="social.socialNetworkInfo" class="tool-account"></el-input>
            <div class="delete-tool-btn" @click="cancelSocialNetwork(social, index)">
              <i class="btn-icon el-icon-close"></i>
            </div>
          </div>
          <el-button class="add-tool-btn" type="text" @click="addSocialNetwork">{{ $t(`${i18nPrefix}.btn.addSocialNetwork`) }}</el-button>
        </div>
      </template>

      <template #mobile="{ line }">
        <div class="mobile-container">
          <el-input
            v-model="stepData.mobile"
            :disabled="line.disabled"
            :class="(isEditMode || isCandidate) ? 'mobile-input': ''"
          />
          <el-button v-if="isEditMode || isCandidate" class="update-mobile" @click="handleModifyMobile" type="text">{{ $t(`${i18nPrefix}.btn.updateMobilePhone`) }}</el-button>
        </div>
      </template>
    </WrappedForm>
    <UpdateMobileDialog
      class="update-mobile-container"
      :captchaIndex="3"
      :phone="stepData.mobile"
      :jobId="jobId"
      :companyId="companyId"
      :isCandidate="isCandidate"
      :countryCode="stepData.mobileCountryCode"
      :show.sync="dialogTableVisible"
      @success="updateMobileSuccess"
    ></UpdateMobileDialog>
    <DialogWrapper
      class="duplicate-talent-candidate-dialog"
      hideFooter
      :width="!stepData.onlyTalentPool && isEn() && '1032px'"
      :titleI18nKey="`${i18nPrefix}.title.duplicateTitle`"
      :show.sync="duplicateCandidateDialog.show"
    >
      <div class="talent-link">
        <div class="tip">{{ duplicateCandidateDialog.title }}</div>
        <el-link :href="duplicateCandidateDialog.link" :underline="false">{{ $t(`${i18nPrefix}.label.duplicateTalentCandidateToView`) }}</el-link>
        <div class="tip">{{ $t(`${i18nPrefix}.label.duplicateTalentCandidateEndTip`) }}</div>
      </div>
    </DialogWrapper>
    <RectLoading v-show="loading" class="loading"></RectLoading>
  </div>
</template>

<script>
import _ from 'lodash'
import mixin from './mixin'
import moment from 'moment'
import UpdateMobileDialog from './updateMobileDialog.vue'
import WrappedForm from '@/components/formEditor/wrappedForm.vue'
import RectLoading from '@/components/loading/rectLoading.vue'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import { addI18nProperty, isEn } from '@/i18n'
import { mapActions, mapGetters } from 'vuex'
import { validateIDCardNumber, validateEmail, validatePhoneByRegexpList } from '@/utils/validator'

export default {
  mixins: [mixin],
  components: {
    WrappedForm,
    UpdateMobileDialog,
    RectLoading,
    DialogWrapper
  },
  props: {
    isEmailEditable: {
      type: Boolean,
      default: true
    },
    isMobileEditable: {
      type: Boolean,
      default: true
    },
    isCompanyJobEditable: {
      type: Boolean,
      default: true
    },
    hiddenIdNumber: {
      type: Boolean,
      default: true
    },
    isHr: {
      type: Boolean,
      default: false
    },
    isAgency: {
      type: Boolean,
      default: false
    },
    setting: {
      type: Object,
      required: false
    },
    isMiniResume: {
      type: Boolean,
      default: false
    },
    isBasicResume: {
      type: Boolean,
      default: false
    },
    hrMiniResumeSetting: {
      type: Object,
      required: false
    },
    hasResumeView: {
      type: Boolean,
      default: false
    },
    companyId: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      isEn,
      dataLoader: null,
      chatToolListInfo: [],
      socialNetworkListInfo: [],
      jobOptionsList: [],
      companyJobLoader: null,
      companyJobList: [],
      stepData: {
        onlyTalentPool: false,
        chatTool: [{ chatValue: null, chatToolInfo: null }],
        socialNetwork: [{ socialValue: null, socialNetworkInfo: null }]
      },
      acceptPhotoFileTypeList: ['image/png', 'image/jpeg', 'image/jpg'],
      photoFile: null,
      photoFilePreviewUrl: null,
      formErrors: {
        email: {
          checked: null,
          error: null
        },
        phone: {
          checked: null,
          error: null
        },
        secondPhone: {
          checked: null,
          error: null
        },
        companyJob: {
          checked: null,
          error: null
        }
      },
      duplicateCandidateList: {
        email: null,
        phone: null,
        secondPhone: null,
        hasChecked: false,
        data: []
      },
      educationLevelList: [],
      schoolList: [],
      school: {
        data: null,
        currentData: null,
        page: 1,
        pageSize: 10
      },
      dialogTableVisible: false,
      duplicateCandidateDialog: {
        title: null,
        show: false,
        link: null
      }
    }
  },
  computed: {
    ...mapGetters('account', ['isCompanyAccount', 'isHeadhunterAccount']),
    loadJobData () {
      if (this.setting?.loadJobData !== undefined) {
        return this.setting?.loadJobData
      }
      return true
    },
    isCandidate () {
      if (this.setting?.isCandidate !== undefined) {
        return this.setting?.isCandidate
      }
      return false
    },
    loadCandidateInfoWhenCreated () {
      if (this.setting?.loadCandidateInfoWhenCreated !== undefined) {
        return this.setting.loadCandidateInfoWhenCreated
      }
      return false
    },
    showUploadButton () {
      if (this.setting?.showUploadButton) {
        return true
      }
      return false
    },
    hiddenField () {
      if (this.isMiniResume || this.isBasicResume) {
        const hiddenField = [
          'salutation',
          'homeLocation',
          'otherAddress',
          'secondPhone',
          'idNumber',
          'chatTool',
          'socialNetwork',
          'personalStatement',
          'expectedSalary'
        ]
        if (this.isBasicResume) {
          hiddenField.push('photo', 'gender', 'birthday', 'currentLocation', 'address')
        }
        if (this.setting?.hiddenField) {
          hiddenField.push(...this.setting?.hiddenField)
        }
        return hiddenField
      }
      return this.setting?.hiddenField || []
    },
    displayField () {
      return this.setting?.displayField || []
    },
    requiredField () {
      return this.setting?.requiredField || []
    },
    disabledField () {
      return this.setting?.disabledField || []
    },
    formLines () {
      const vm = this
      const requiredMessage = this.$t('common.message.requiredFormField')
      const requiredField = ['companyJob', 'firstName', 'lastName']
      if (this.requiredField) {
        requiredField.push(...this.requiredField)
      }
      const hiddenField = []
      if (this.hiddenField) {
        hiddenField.push(...this.hiddenField)
      }
      const displayField = []
      if (this.displayField) {
        displayField.push(...this.displayField)
      }
      const disabledField = []
      if (this.disabledField) {
        disabledField.push(...this.disabledField)
      }
      const limitDate = moment().subtract(18, 'year')
      const lines = {
        onlyTalentPool: {
          elType: 'el-radio-group',
          get hidden () { return hiddenField.includes('onlyTalentPool') || !vm.isCompanyJobEditable },
          class: 'only-talent-pool',
          label: this.$t(`${this.i18nPrefix}.label.onlyTalentPool`),
          option: {
            data: [{ label: this.$t('common.label.yes'), value: true }, { label: this.$t('common.label.no'), value: false }]
          },
          rules: [{ required: true, message: requiredMessage, trigger: 'blur' }],
          events: { change: this.handleOnlyTalentPoolChange }
        },
        companyJob: {
          get hidden () { return hiddenField.includes('companyJob') || (vm.stepData.onlyTalentPool && !displayField.includes('companyJob')) },
          get disabled () { return !vm.isCompanyJobEditable || disabledField.includes('companyJob') },
          elType: 'el-select',
          placeholder: this.$t(`${this.i18nPrefix}.label.selectCandidate`),
          label: this.$t(`${this.i18nPrefix}.label.addCandidateTo`),
          option: {
            data: this.companyJobList,
            grouped: true,
            groupLabelKey: 'i18nName',
            groupOptionsDataKey: 'job_list',
            valueKey: 'id',
            labelKey: 'i18nName'
          },
          props: { clearable: true, filterable: true },
          events: { change: this.handleCompanyJobChange },
          rules: [
            { required: requiredField.includes('companyJob'), message: requiredMessage, trigger: 'change' },
            { validator: this.validateCompanyJob, trigger: 'change' }
          ]
        },
        photo: {
          disabled: disabledField.includes('photo'),
          contentSlot: 'uploadPhoto',
          label: this.$t(`${this.i18nPrefix}.label.photo`),
          hidden: hiddenField.includes('photo') && !displayField.includes('photo'),
          rules: [{ required: requiredField.includes('photo'), message: requiredMessage, trigger: 'change' }]
        },
        emailAddress: {
          get disabled () { return !vm.isEmailEditable || disabledField.includes('emailAddress') },
          class: 'email-form-line',
          label: this.$t(`${this.i18nPrefix}.label.emailAddress`),
          hidden: hiddenField.includes('emailAddress') && !displayField.includes('emailAddress'),
          rules: [
            { validator: this.validateEmailAddress, trigger: 'blur' }
          ]
        },
        firstName: {
          disabled: disabledField.includes('firstName'),
          label: this.$t(`${this.i18nPrefix}.label.firstName`),
          hidden: hiddenField.includes('firstName') && !displayField.includes('firstName'),
          rules: [{ required: requiredField.includes('firstName'), message: requiredMessage, trigger: 'change' }]
        },
        lastName: {
          disabled: disabledField.includes('lastName'),
          label: this.$t(`${this.i18nPrefix}.label.lastName`),
          hidden: hiddenField.includes('lastName') && !displayField.includes('lastName'),
          rules: [{ required: requiredField.includes('lastName'), message: requiredMessage, trigger: 'change' }]
        },
        salutation: {
          elType: 'el-select',
          disabled: disabledField.includes('salutation'),
          label: this.$t(`${this.i18nPrefix}.label.salutation`),
          hidden: hiddenField.includes('salutation') && !displayField.includes('salutation'),
          option: {
            data: [
              { label: this.$t(`${this.i18nPrefix}.label.Mr`), value: 'MR' },
              { label: this.$t(`${this.i18nPrefix}.label.Mrs`), value: 'MRS' },
              { label: this.$t(`${this.i18nPrefix}.label.Ms`), value: 'MS' }
            ]
          },
          events: { change: this.handleSalutationChange },
          rules: [{ required: requiredField.includes('salutation'), message: requiredMessage, trigger: 'change' }]
        },
        gender: {
          elType: 'el-select',
          disabled: disabledField.includes('gender'),
          label: this.$t(`${this.i18nPrefix}.label.gender`),
          hidden: hiddenField.includes('gender') && !displayField.includes('gender'),
          option: {
            data: [
              { label: this.$t('common.gender.male'), value: 'M' },
              { label: this.$t('common.gender.female'), value: 'F' },
              { label: this.$t('common.gender.nonBinary'), value: 'N' },
              { label: this.$t('common.gender.preferNotToDisclose'), value: 'P' }
            ]
          },
          clearOnChange: ['salutation'],
          rules: [{ required: requiredField.includes('gender'), message: requiredMessage, trigger: 'change' }]
        },
        birthday: {
          elType: 'el-date-picker',
          disabled: disabledField.includes('birthday'),
          label: this.$t(`${this.i18nPrefix}.label.birthday`),
          hidden: hiddenField.includes('birthday') && !displayField.includes('birthday'),
          defaultValue: limitDate,
          props: {
            type: 'date',
            clearable: false,
            valueFormat: 'yyyy-MM-dd',
            pickerOptions: {
              disabledDate (time) {
                return time.valueOf() > limitDate.valueOf()
              }
            }
          },
          rules: [{ required: requiredField.includes('birthday'), message: requiredMessage, trigger: 'change' }]
        },
        homeLocation: {
          disabled: disabledField.includes('homeLocation'),
          label: this.$t(`${this.i18nPrefix}.label.homeLocation`),
          hidden: hiddenField.includes('homeLocation') && !displayField.includes('homeLocation'),
          class: 'home-form-line',
          hiddenPropName: false,
          rules: [{ required: requiredField.includes('homeLocation'), validator: this.emptyValidate }],
          multiple: {
            country: {
              elType: 'el-select',
              class: 'county-select',
              placeholder: this.$t(`${this.i18nPrefix}.label.country`),
              option: {
                delayLoadData: this.loadHomeCountryList
              },
              props: { filterable: true },
              clearOnChange: ['province', 'city'],
              rules: [{ required: requiredField.includes('homeLocation'), message: requiredMessage, trigger: 'change' }]
            },
            province: {
              elType: 'el-select',
              class: 'province-select',
              placeholder: this.$t(`${this.i18nPrefix}.label.province`),
              option: {
                reloadIfUnmatched: true,
                delayLoadData: this.loadHomeProvinceList
              },
              props: { filterable: true },
              clearOnChange: ['city'],
              rules: [{ required: requiredField.includes('homeLocation'), message: requiredMessage, trigger: 'change' }]
            },
            city: {
              elType: 'el-select',
              class: 'city-select',
              placeholder: this.$t(`${this.i18nPrefix}.label.city`),
              option: {
                reloadIfUnmatched: true,
                delayLoadData: this.loadHomeCityList
              },
              props: { filterable: true },
              events: { change: this.handleHomeCityChange },
              rules: [{ required: requiredField.includes('homeLocation'), message: requiredMessage, trigger: 'change' }]
            }
          }
        },
        currentLocation: {
          disabled: disabledField.includes('currentLocation'),
          label: this.$t(`${this.i18nPrefix}.label.currentLocation`),
          hidden: hiddenField.includes('currentLocation') && !displayField.includes('currentLocation'),
          class: 'home-form-line',
          hiddenPropName: false,
          rules: [{ required: requiredField.includes('currentLocation'), validator: this.emptyValidate }],
          multiple: {
            currentCountry: {
              elType: 'el-select',
              class: 'county-select',
              placeholder: this.$t(`${this.i18nPrefix}.label.currentCountry`),
              option: {
                delayLoadData: this.loadCurrentCountryList
              },
              props: { filterable: true },
              clearOnChange: ['currentProvince', 'currentCity'],
              rules: [{ required: requiredField.includes('currentLocation'), message: requiredMessage, trigger: 'change' }]
            },
            currentProvince: {
              elType: 'el-select',
              class: 'province-select',
              placeholder: this.$t(`${this.i18nPrefix}.label.currentProvince`),
              option: {
                reloadIfUnmatched: true,
                delayLoadData: this.loadCurrentProvinceList
              },
              props: { filterable: true },
              clearOnChange: ['currentCity'],
              rules: [{ required: requiredField.includes('currentLocation'), message: requiredMessage, trigger: 'change' }]
            },
            currentCity: {
              elType: 'el-select',
              class: 'city-select',
              placeholder: this.$t(`${this.i18nPrefix}.label.currentCity`),
              option: {
                reloadIfUnmatched: true,
                delayLoadData: this.loadCurrentCityList
              },
              props: { filterable: true },
              events: { change: this.handleCurrentCityChange },
              rules: [{ required: requiredField.includes('currentLocation'), message: requiredMessage, trigger: 'change' }]
            }
          }
        },
        address: {
          disabled: disabledField.includes('address'),
          label: this.$t(`${this.i18nPrefix}.label.address`),
          hidden: hiddenField.includes('address') && !displayField.includes('address'),
          rules: [{ required: requiredField.includes('address'), message: requiredMessage, trigger: 'change' }]
        },
        otherAddress: {
          disabled: disabledField.includes('otherAddress'),
          label: this.$t(`${this.i18nPrefix}.label.otherAddressIfHave`),
          hidden: hiddenField.includes('otherAddress') && !displayField.includes('otherAddress'),
          rules: [{ required: requiredField.includes('otherAddress'), message: requiredMessage, trigger: 'change' }]
        },
        mobile: {
          disabled: this.isEditMode || disabledField.includes('mobile'),
          class: 'phone-form-line',
          contentSlot: 'mobile',
          label: this.$t(`${this.i18nPrefix}.label.mobile`),
          hidden: hiddenField.includes('mobile') && !displayField.includes('mobile'),
          rules: [
            { validator: this.validateMobile, trigger: 'blur' }
          ]
        },
        secondPhone: {
          disabled: disabledField.includes('secondPhone'),
          class: 'second-phone-form-line',
          label: this.$t(`${this.i18nPrefix}.label.secondPhone`),
          hidden: hiddenField.includes('secondPhone') && !displayField.includes('secondPhone'),
          rules: [{ validator: this.validateSecondPhone, trigger: 'blur' }]
        },
        idNumber: {
          disabled: disabledField.includes('idNumber'),
          get hidden () { return vm.hiddenIdNumber },
          label: this.$t(`${this.i18nPrefix}.label.idNumber`),
          rules: [{ validator: validateIDCardNumber, trigger: 'blur' }]
        },
        chatTool: {
          elType: 'div',
          disabled: disabledField.includes('chatTool'),
          label: this.$t(`${this.i18nPrefix}.label.chatId`),
          hidden: hiddenField.includes('chatTool') && !displayField.includes('chatTool'),
          contentSlot: 'chatTool',
          rules: [{ required: requiredField.includes('chatTool'), message: requiredMessage, trigger: 'change' }]
        },
        socialNetwork: {
          elType: 'div',
          disabled: disabledField.includes('socialNetwork'),
          label: this.$t(`${this.i18nPrefix}.label.socialNetwork`),
          hidden: hiddenField.includes('socialNetwork') && !displayField.includes('socialNetwork'),
          contentSlot: 'socialNetwork',
          rules: [{ required: requiredField.includes('socialNetwork'), message: requiredMessage, trigger: 'change' }]
        },
        personalStatement: {
          disabled: disabledField.includes('personalStatement'),
          label: this.$t(`${this.i18nPrefix}.label.personalStatement`),
          hidden: hiddenField.includes('personalStatement') && !displayField.includes('personalStatement'),
          placeholder: this.$t(`${this.i18nPrefix}.btn.personalStatementInfoDescription`),
          props: { type: 'textarea', autosize: { minRows: 5 } },
          rules: [{ required: requiredField.includes('personalStatement'), message: requiredMessage, trigger: 'change' }]
        },
        expectedSalary: {
          disabled: disabledField.includes('expectedSalary'),
          label: this.$t(`${this.i18nPrefix}.label.expectedSalary`),
          hidden: hiddenField.includes('expectedSalary') && !displayField.includes('expectedSalary'),
          class: 'salary-form-line',
          hiddenPropName: true,
          contentExtraSlot: 'expectedSalaryInfo',
          multiple: {
            currency: {
              elType: 'el-select',
              class: 'currency-select',
              props: { clearable: true },
              option: {
                delayLoadData: this.loadCurrencyList,
                labelKey: 'label',
                valueKey: 'value'
              }
            },
            salary: {
              elType: 'el-select',
              class: 'salary-select',
              props: { clearable: true },
              option: {
                delayLoadData: this.loadSalaryList
              }
            }
          }
        }
      }
      if (this.requiredField.includes('emailAddress')) {
        lines.emailAddress.rules.splice(0, 0, { required: true, message: requiredMessage, trigger: 'change' })
      }
      if (this.requiredField.includes('mobile')) {
        lines.mobile.rules.splice(0, 0, { required: true, message: requiredMessage, trigger: 'change' })
      }
      return lines
    },
    hasOnlyActionOnMyCandidatePermission () {
      return this.hasAnyPermission('PERM_ONLY_ACTION_ON_MY_CANDIDATE')
    }
  },
  created () {
    if (this.loadCandidateInfoWhenCreated) {
      this.resetForm()
    } else {
      this.loadData()
    }
  },
  methods: {
    ...mapActions('common', ['getCurrencyList']),
    ...mapActions('personal', ['checkEmailAndPhone']),
    ...mapActions('worldLocation', ['getCountryList', 'getProvinceList', 'getCityList']),
    ...mapActions('application', ['getChatToolList', 'getSocialNetworkList', 'getSalaryList', 'getHeadhunterUploadedCandidateCount', 'getDuplicateCandidateList', 'checkEmailOrPhoneApplication', 'getEducationLevelList', 'getSchoolList']),
    ...mapActions('company', ['getCompanyGroupedJobList']),
    ...mapActions('talent', ['getTalentDuplicateCandidateIds']),
    handleHrSwitchMiniResumeChange (value) {
      this.$emit('hrSwitchMiniResumeChange', value)
    },
    formatSimpleJobInfo (infoItem) {
      const info = {
        ...infoItem,
        value: infoItem.id || infoItem.value
      }
      addI18nProperty(info, 'label', 'cn_name', 'name')
      return info
    },
    handleOnlyTalentPoolChange (onlyTalentPool) {
      if (onlyTalentPool) {
        this.stepData.companyJob = null
      }
      this.$emit('handleOnlyTalentPoolChange', onlyTalentPool)
    },
    handleCompanyJobChange (companyJob) {
      this.$emit('handleCompanyJobChange', companyJob)
    },
    async loadData () {
      if (!this.dataLoader) {
        if (this.isMiniResume) {
          this.dataLoader = Promise.all([
            this.loadCompanyJob(),
            this.loadChatToolList(),
            this.loadSocialNetworkList(),
            this.loadEducationLevelList()
          ])
        } else if (this.isBasicResume) {
          this.dataLoader = Promise.all([])
        } else {
          this.dataLoader = Promise.all([
            this.loadCompanyJob(),
            this.loadChatToolList(),
            this.loadSocialNetworkList()
          ])
        }
      }
      await this.dataLoader
    },
    async loadCompanyJob () {
      if (!this.companyJobLoader && this.loadJobData) {
        const params = {
          status: 1,
          add_candidate: 1
        }
        this.companyJobLoader = this.getCompanyGroupedJobList(params).then(resp => {
          this.companyJobList = _.map(resp, company => {
            addI18nProperty(company, 'i18nName', 'cn_name', 'name')
            company.job_list = _.map(company.job_list, job => {
              job.cn_title = `${job.id} - ${job.cn_title}`
              job.title = `${job.id} - ${job.title}`
              addI18nProperty(job, 'i18nName', 'cn_title', 'title')
              return job
            })
            return company
          })
        })
      }
      await this.companyJobLoader
      return this.companyJobList
    },
    handleSalutationChange (salutation) {
      if (salutation) {
        if (salutation === 'MR') {
          this.stepData.gender = 'M'
        } else {
          this.stepData.gender = 'F'
        }
      } else {
        this.stepData.gender = null
      }
    },
    async loadHomeCountryList () {
      const resp = await this.getCountryList()
      if (resp) {
        this.homeCountryList = _.map(resp.location_list, this.formatSimpleJobInfo)
      }
      return this.homeCountryList
    },
    async loadHomeProvinceList () {
      if (this.stepData.country && !isNaN(this.stepData.country)) {
        const params = {
          parentId: this.stepData.country
        }
        const resp = await this.getProvinceList(params)
        if (resp) {
          this.homeProvinceList = _.map(resp.location_list, this.formatSimpleJobInfo)
        }
        return this.homeProvinceList
      } else {
        return []
      }
    },
    handleHomeCityChange (homeCity) {
      this.$set(this.stepData, this.stepData.city, homeCity)
    },
    async loadHomeCityList () {
      if (this.stepData.province && !isNaN(this.stepData.province)) {
        const params = {
          parentId: this.stepData.province
        }
        const resp = await this.getCityList(params)
        if (resp) {
          this.homeCityList = _.map(resp.location_list, this.formatSimpleJobInfo)
        }
        return this.homeCityList
      } else {
        return []
      }
    },
    async loadCurrentCountryList () {
      const resp = await this.getCountryList()
      if (resp) {
        this.homeCountryList = _.map(resp.location_list, this.formatSimpleJobInfo)
      }
      return this.homeCountryList
    },
    async loadCurrentProvinceList () {
      if (this.stepData.currentCountry && !isNaN(this.stepData.currentCountry)) {
        const params = {
          parentId: this.stepData.currentCountry
        }
        const resp = await this.getProvinceList(params)
        if (resp) {
          this.currentProvinceList = _.map(resp.location_list, this.formatSimpleJobInfo)
        }
        return this.currentProvinceList
      } else {
        return []
      }
    },
    handleCurrentCityChange (currentCity) {
      this.$set(this.stepData, this.stepData.currentCity, currentCity)
    },
    async loadCurrentCityList () {
      if (this.stepData.currentProvince && !isNaN(this.stepData.currentProvince)) {
        const params = {
          parentId: this.stepData.currentProvince
        }
        const resp = await this.getCityList(params)
        if (resp) {
          this.currentCityList = _.map(resp.location_list, this.formatSimpleJobInfo)
        }
        return this.currentCityList
      } else {
        return []
      }
    },
    async loadChatToolList () {
      const resp = await this.getChatToolList()
      if (resp) {
        this.chatToolListInfo = _.map(resp.chat_tools, item => {
          addI18nProperty(item, 'i18nName', 'cn_name', 'en_name')
          return item
        })
      }
    },
    async loadSocialNetworkList () {
      const resp = await this.getSocialNetworkList()
      if (resp) {
        this.socialNetworkListInfo = _.map(resp.social_networks, item => {
          addI18nProperty(item, 'i18nName', 'cn_name', 'en_name')
          return item
        })
      }
    },
    async loadCurrencyList () {
      return this.getCurrencyList().then(resp => {
        if (!resp) {
          return []
        }
        const currencyList = _.map(resp, (item) => {
          return { id: item.id, label: `${item.name}(${item.value})`, value: item.name }
        })
        this.currencyList = currencyList
        return currencyList
      })
    },
    async loadSalaryList () {
      const resp = await this.getSalaryList()
      if (resp) {
        this.salaryList = _.map(resp.salary_list, this.formatSimpleJobInfo)
      }
      return this.salaryList
    },
    async loadEducationLevelList () {
      const resp = await this.getEducationLevelList()
      if (resp) {
        this.educationLevelList = _.map(resp.education_levels, item => {
          addI18nProperty(item, 'i18nName', 'cn_name', 'en_name')
          return item
        })
      }
    },
    async searchSchoolList (keywords) {
      if (!keywords || !keywords.trim()) {
        return
      }
      const params = {
        keywords: keywords
      }
      const resp = await this.getSchoolList(params)
      if (resp) {
        this.schoolList = _.map(resp.schools, item => {
          addI18nProperty(item, 'value', 'cn_name', 'name')
          return item
        })
      }
      return this.schoolList
    },
    onSchoolSelect (selected) {
      const school = _.find(this.schoolList, { value: selected })
      if (school) {
        this.stepData.schoolId = school.id
      } else {
        this.stepData.schoolId = null
      }
    },
    addChatTool () {
      this.stepData.chatTool.push({ chatValue: null, chatToolInfo: null })
    },
    cancelChat (chatTool, index) {
      this.stepData.chatTool.splice(index, 1)
    },
    addSocialNetwork () {
      this.stepData.socialNetwork.push({ socialValue: null, socialNetworkInfo: null })
    },
    cancelSocialNetwork (social, index) {
      this.stepData.socialNetwork.splice(index, 1)
    },
    handlePhotoSelectBtnClick () {
      this.$refs.fileSelector.click()
    },
    handlePhotoSelectorChange () {
      const file = this.$refs.fileSelector.files[0]
      if (!file) {
        return
      }
      if (!this.acceptPhotoFileTypeList.includes(file.type)) {
        this.$message.error({
          type: 'error',
          showClose: true,
          message: this.$t(`${this.i18nPrefix}.message.wrongPhotoFileType`)
        })
        return true
      }
      this.photoFile = file
      this.stepData.photo = window.URL.createObjectURL(file)
    },
    formatNullChatToolOrSocialNetwork (data) {
      if (data.chatValue === 'null') {
        data.chatValue = null
      }
      if (data.socialValue === 'null') {
        data.socialValue = null
      }
      return data
    },
    async emptyValidate (rule, value, callback) {
      callback()
    },
    async validateEmailAddress (rule, value, callback) {
      const formError = this.formErrors.email
      const checked = formError.checked === value
      if (checked) {
        if (formError.error) {
          if (formError.isDuplicateError) {
            this.duplicateCandidateDialog.show = true
            callback()
          } else {
            callback(new Error(formError.error))
          }
        } else {
          callback()
        }
        return
      }
      this.count += 1
      const email = value
      const phone = this.stepData.mobile
      const secondPhone = this.stepData.secondPhone
      let errMsg = null
      let checkMobile = false
      this.loading = true
      let hiddenErrorMsg = false
      try {
        if (email) {
          errMsg = validateEmail(rule, email)
        } else if (!phone) {
          if (this.requiredField.includes('mobile') && this.requiredField.includes('emailAddress')) {
            errMsg = this.$t('common.message.requiredFormField')
          } else {
            errMsg = this.$t(`${this.i18nPrefix}.message.needOneOfEmailOrMobile`)
          }
        } else {
          checkMobile = true
          this.formErrors.phone.checked = null
          await this.$refs.form.validateField('mobile')
        }
        if (value && !errMsg && !this.isEditMode && !this.isCandidate) {
          const resp = email && await this.checkCanApplyJob(email, phone)
          if (!resp.can_apply) {
            errMsg = this.$t(`${this.i18nPrefix}.message.isHrEmailOrPhone`)
          } else if (resp.has_duplicate) {
            const duplicateInfo = resp.duplicate_info[0]
            this.duplicateCandidateDialog.title = this.$t(`${this.i18nPrefix}.label.hasDuplicateCandidate`)
            this.duplicateCandidateDialog.show = true
            this.duplicateCandidateDialog.link = `/v-admin/application/profile/resume?candidate_id=${duplicateInfo.candidate_id}&job_id=${duplicateInfo.job_id}`
            errMsg = this.$t(`${this.i18nPrefix}.message.hasDuplicateCandidate`)
            hiddenErrorMsg = true
          } else if (this.isHeadhunterAccount) {
            const hasInTalentPool = await this.hasInTalentPool(email, phone, secondPhone)
            if (hasInTalentPool) {
              errMsg = this.$t(`${this.i18nPrefix}.message.hasUploadedInTalentPool`)
            }
          }
          if (!this.isCandidate && this.stepData.onlyTalentPool) {
            const duplicateCandidateIds = await this.getDuplicateTalentCandidateIdList()
            if (duplicateCandidateIds?.length) {
              this.duplicateCandidateDialog.title = this.$t(`${this.i18nPrefix}.label.duplicateTalentCandidatePrevTip`)
              this.duplicateCandidateDialog.show = true
              this.duplicateCandidateDialog.link = `/v-admin/talent-pool/candidate/profile/resume?candidate_id=${duplicateCandidateIds[0]}`
              errMsg = this.$t(`${this.i18nPrefix}.message.hasUploadedInTalentPool`)
              hiddenErrorMsg = true
            }
          }
        } else if (value && !errMsg && this.isEmailEditable && this.isCandidate) {
          const hasUse = await this.checkEmailAndPhone({ email, phone })
          if (hasUse) {
            errMsg = this.$t(`${this.i18nPrefix}.message.emailHasAlreadyUsed`)
          }
        }
      } finally {
        formError.checked = email
        formError.error = errMsg
        if (errMsg) {
          if (hiddenErrorMsg) {
            formError.isDuplicateError = true
            callback()
          } else {
            callback(new Error(errMsg))
          }
        } else {
          if (!phone) {
            this.formErrors.phone.error = null
          }
          if (value) {
            const hasDuplicateErrorMsg = this.$t(`${this.i18nPrefix}.message.hasDuplicateCandidate`)
            const companyJobFormError = this.formErrors.companyJob
            const phoneFormError = this.formErrors.phone
            if (companyJobFormError.error === hasDuplicateErrorMsg) {
              companyJobFormError.error = null
              this.$refs.form.clearValidate('companyJob')
            }
            if (phoneFormError.error === hasDuplicateErrorMsg) {
              phoneFormError.error = null
              this.$refs.form.clearValidate('mobile')
            }
          }
          callback()
        }
        if (!checkMobile) {
          this.loading = false
        }
      }
    },
    async getDuplicateTalentCandidateIdList () {
      const email = this.stepData.emailAddress
      const mobile = this.stepData.mobile
      const secondPhone = this.stepData.secondPhone
      const candidateId = this.isEditMode ? this.candidateInfo.baseInfo.candidate_id : null
      if (!email && !mobile && !secondPhone) {
        return
      }
      const params = {
        email: email,
        phone: mobile,
        second_phone: secondPhone,
        candidate_id: candidateId
      }
      const resp = await this.getTalentDuplicateCandidateIds(params)
      return resp
    },
    async validateCompanyJob (rule, value, callback) {
      if (this.formLines.companyJob.disabled) {
        return callback()
      }
      const formError = this.formErrors.companyJob
      const checked = formError.checked === value
      if (checked) {
        if (formError.error) {
          callback(new Error(formError.error))
        } else {
          callback()
        }
        return
      }
      this.loading = true
      let errMsg = null
      let hiddenErrorMsg = false
      try {
        if (!this.isEditMode && !this.isCandidate) {
          const resp = await this.checkCanApplyJob(this.stepData.emailAddress, this.stepData.mobile)
          if (resp.has_duplicate) {
            const duplicateInfo = resp.duplicate_info[0]
            this.duplicateCandidateDialog.title = this.$t(`${this.i18nPrefix}.label.hasDuplicateCandidate`)
            this.duplicateCandidateDialog.show = true
            this.duplicateCandidateDialog.link = `/v-admin/application/profile/resume?candidate_id=${duplicateInfo.candidate_id}&job_id=${duplicateInfo.job_id}`
            errMsg = this.$t(`${this.i18nPrefix}.message.hasDuplicateCandidate`)
            hiddenErrorMsg = true
          }
        }
      } finally {
        formError.checked = value
        formError.error = errMsg
        if (errMsg) {
          if (hiddenErrorMsg) {
            formError.isDuplicateError = true
            callback()
          } else {
            callback(new Error(errMsg))
          }
        } else {
          if (value) {
            const hasDuplicateErrorMsg = this.$t(`${this.i18nPrefix}.message.hasDuplicateCandidate`)
            const emailFormError = this.formErrors.email
            const phoneFromError = this.formErrors.phone
            if (emailFormError.error === hasDuplicateErrorMsg) {
              emailFormError.error = null
              this.$refs.form.clearValidate('emailAddress')
            }
            if (phoneFromError.error === hasDuplicateErrorMsg) {
              phoneFromError.error = null
              this.$refs.form.clearValidate('mobile')
            }
          }
          callback()
        }
        this.loading = false
      }
    },
    async validateMobile (rule, value, callback) {
      const formError = this.formErrors.phone
      const checked = formError.checked === value
      if (checked) {
        if (formError.error) {
          if (formError.isDuplicateError) {
            this.duplicateCandidateDialog.show = true
            callback()
          } else {
            callback(new Error(formError.error))
          }
        } else {
          callback()
        }
        return
      }
      const email = this.stepData.emailAddress
      const phone = value
      const secondPhone = this.stepData.secondPhone
      let errMsg = null
      let hiddenErrorMsg = false
      this.loading = true
      try {
        if (phone) {
          errMsg = validatePhoneByRegexpList(rule, value)
        } else if (!email) {
          if (this.requiredField.includes('mobile') && this.requiredField.includes('emailAddress')) {
            errMsg = this.$t('common.message.requiredFormField')
          } else {
            errMsg = this.$t(`${this.i18nPrefix}.message.needOneOfEmailOrMobile`)
          }
        }
        if (value && !errMsg && !this.isEditMode && !this.isCandidate) {
          const resp = phone && await this.checkCanApplyJob(email, phone)
          if (!resp.can_apply) {
            errMsg = this.$t(`${this.i18nPrefix}.message.isHrEmailOrPhone`)
          } else if (resp.has_duplicate) {
            const duplicateInfo = resp.duplicate_info[0]
            this.duplicateCandidateDialog.title = this.$t(`${this.i18nPrefix}.label.hasDuplicateCandidate`)
            this.duplicateCandidateDialog.show = true
            this.duplicateCandidateDialog.link = `/v-admin/application/profile/resume?candidate_id=${duplicateInfo.candidate_id}&job_id=${duplicateInfo.job_id}`
            errMsg = this.$t(`${this.i18nPrefix}.message.hasDuplicateCandidate`)
            hiddenErrorMsg = true
          } else if (this.isHeadhunterAccount) {
            const hasInTalentPool = await this.hasInTalentPool(email, phone, secondPhone)
            if (hasInTalentPool) {
              errMsg = this.$t(`${this.i18nPrefix}.message.hasUploadedInTalentPool`)
            }
          }
          if (!this.isCandidate && this.stepData.onlyTalentPool) {
            const duplicateCandidateIds = await this.getDuplicateTalentCandidateIdList()
            if (duplicateCandidateIds?.length) {
              this.duplicateCandidateDialog.title = this.$t(`${this.i18nPrefix}.label.duplicateTalentCandidatePrevTip`)
              this.duplicateCandidateDialog.show = true
              this.duplicateCandidateDialog.link = `/v-admin/talent-pool/candidate/profile/resume?candidate_id=${duplicateCandidateIds[0]}`
              errMsg = this.$t(`${this.i18nPrefix}.message.hasUploadedInTalentPool`)
              hiddenErrorMsg = true
            }
          }
        }
      } finally {
        formError.checked = phone
        formError.error = errMsg
        if (errMsg) {
          if (hiddenErrorMsg) {
            formError.isDuplicateError = true
            callback()
          } else {
            callback(new Error(errMsg))
          }
        } else {
          if (!email) {
            this.formErrors.email.error = null
          }
          if (value) {
            const hasDuplicateErrorMsg = this.$t(`${this.i18nPrefix}.message.hasDuplicateCandidate`)
            const companyJobFormError = this.formErrors.companyJob
            const emailFormError = this.formErrors.email
            if (companyJobFormError.error === hasDuplicateErrorMsg) {
              companyJobFormError.error = null
              this.$refs.form.clearValidate('companyJob')
            }
            if (emailFormError.error === hasDuplicateErrorMsg) {
              emailFormError.error = null
              this.$refs.form.clearValidate('emailAddress')
            }
          }
          callback()
        }
        this.loading = false
      }
    },
    async validateSecondPhone (rule, value, callback) {
      const formError = this.formErrors.secondPhone
      const checked = formError.checked === value
      if (checked) {
        if (formError.error) {
          if (formError.isDuplicateError) {
            this.duplicateCandidateDialog.show = true
            callback()
          } else {
            callback(new Error(formError.error))
          }
        } else {
          callback()
        }
        return
      }
      const email = this.stepData.emailAddress
      const phone = this.stepData.mobile
      const secondPhone = value
      let errMsg = null
      let hiddenErrorMsg = false
      this.loading = true
      try {
        if (value && !this.isEditMode) {
          if (this.isHeadhunterAccount) {
            const hasInTalentPool = await this.hasInTalentPool(email, phone, secondPhone)
            if (hasInTalentPool) {
              errMsg = this.$t(`${this.i18nPrefix}.message.hasUploadedInTalentPool`)
            }
          } else if (!this.isCandidate && this.stepData.onlyTalentPool) {
            const duplicateCandidateIds = await this.getDuplicateTalentCandidateIdList()
            if (duplicateCandidateIds?.length) {
              this.duplicateCandidateDialog.title = this.$t(`${this.i18nPrefix}.label.duplicateTalentCandidatePrevTip`)
              this.duplicateCandidateDialog.show = true
              this.duplicateCandidateDialog.link = `/v-admin/talent-pool/candidate/profile/resume?candidate_id=${duplicateCandidateIds[0]}`
              errMsg = this.$t(`${this.i18nPrefix}.message.hasUploadedInTalentPool`)
              hiddenErrorMsg = true
            }
          }
        }
      } finally {
        formError.checked = secondPhone
        formError.error = errMsg
        if (errMsg) {
          if (hiddenErrorMsg) {
            formError.isDuplicateError = true
            callback()
          } else {
            callback(new Error(errMsg))
          }
        } else {
          callback()
        }
        this.loading = false
      }
    },
    async hasInTalentPool (email, phone, secondPhone) {
      if (!email && !phone && !secondPhone) {
        return
      }
      const jobId = this.$route.params.jobId
      const params = {
        email: email,
        phone: phone,
        second_phone: secondPhone,
        job_id: jobId
      }
      const uploadedCount = await this.getHeadhunterUploadedCandidateCount(params)
      return uploadedCount && uploadedCount > 0
    },
    async checkCanApplyJob (email, phone) {
      if (this.isCandidate) {
        return false
      }
      if (!email && !phone) {
        return
      }
      const params = {
        email: email,
        phone: phone,
        job_id: this.stepData.companyJob,
        candidate_avatar_id: this.candidateInfo.avatarId
      }
      const resp = await this.checkEmailOrPhoneApplication(params)
      return resp
    },
    async loadDuplicateCandidate (email, phone, secondPhone) {
      if (!email && !phone && !secondPhone) {
        return
      }
      const duplicateCandidateList = this.duplicateCandidateList
      if (duplicateCandidateList.email === email &&
        duplicateCandidateList.phone === phone &&
        duplicateCandidateList.secondPhone === secondPhone) {
        return
      }
      duplicateCandidateList.email = email
      duplicateCandidateList.phone = phone
      duplicateCandidateList.secondPhone = secondPhone
      duplicateCandidateList.hasChecked = false
      let candidateId = null
      if (this.isEditMode) {
        candidateId = this.candidateInfo.baseInfo.candidate_id
      }
      const params = {
        email: duplicateCandidateList.email,
        phone: duplicateCandidateList.phone,
        second_phone: duplicateCandidateList.secondPhone,
        candidate_id: candidateId
      }
      const resp = await this.getDuplicateCandidateList(params)
      if (resp) {
        this.duplicateCandidateList.data = resp
        if (resp?.length < 1) {
          this.duplicateCandidateList.hasChecked = true
        }
        const duplicateCandidateChangePromise = new Promise((resolve) => {
          this.$emit('duplicateCandidateChange', this.duplicateCandidateList.data, resolve)
        })
        await duplicateCandidateChangePromise
      }
    },
    async customValidate () {
      if (!this.isCompanyAccount) {
        return true
      }
      if (this.duplicateCandidateDialog.show) {
        return false
      }
      const email = this.stepData.emailAddress
      const phone = this.stepData.mobile
      const secondPhone = this.stepData.secondPhone
      await this.loadDuplicateCandidate(email, phone, secondPhone)

      if (!this.hrMiniResumeSetting.isHrMiniResume) {
        return true
      }
      if (this.duplicateCandidateList.hasChecked) {
        return true
      }

      this.duplicateCandidateList.hasChecked = true
      return this.duplicateCandidateList.data?.length < 1
    },
    async setStepDataToCandidateInfo () {
      const stepData = this.stepData
      const candidateInfo = this.candidateInfo
      const chatToolList = _.filter(stepData.chatTool, item => item.chatValue && item.chatToolInfo)
      const socialNetworkList = _.filter(stepData.socialNetwork, item => item.socialValue && item.socialNetworkInfo)
      this.$set(candidateInfo, 'onlyTalentPool', stepData.onlyTalentPool)
      const fieldMapping = {
        companyJob: stepData.companyJob,
        emailAddress: stepData.emailAddress,
        firstName: stepData.firstName,
        lastName: stepData.lastName,
        salutation: stepData.salutation,
        gender: stepData.gender,
        birthday: stepData.birthday,
        country: stepData.country,
        province: stepData.province,
        city: stepData.city,
        currentCountry: stepData.currentCountry,
        currentProvince: stepData.currentProvince,
        currentCity: stepData.currentCity,
        address: stepData.address,
        otherAddress: stepData.otherAddress,
        mobile: stepData.mobile,
        secondPhone: stepData.secondPhone,
        idNumber: stepData.idNumber,
        chatTool: chatToolList,
        socialNetwork: socialNetworkList,
        personalStatement: stepData.personalStatement,
        currency: stepData.currency,
        salary: stepData.salary,
        photo: stepData.photo,
        photoFile: this.photoFile,
        originalFileType: candidateInfo?.baseInfo?.originalFileType
      }
      fieldMapping.mobileCountryCode = stepData.mobileCountryCode
      this.$set(candidateInfo, 'resumePath', candidateInfo.resumePath || candidateInfo.baseInfo.resume_path)
      this.$set(candidateInfo, 'baseInfo', fieldMapping)
    },
    async getStepData () {
      await this.loadData()
      const baseInfo = this.candidateInfo.baseInfo || {}
      const chatToolList = _.map(baseInfo.chatTool || [], this.formatNullChatToolOrSocialNetwork)
      const socialNetworkList = _.map(baseInfo.socialNetwork || [], this.formatNullChatToolOrSocialNetwork)
      if (chatToolList.length < 1) {
        chatToolList.push({ chatValue: null, chatToolInfo: null })
      }
      if (socialNetworkList.length < 1) {
        socialNetworkList.push({ socialValue: null, socialNetworkInfo: null })
      }
      let onlyTalentPool = false
      if (this.isCompanyJobEditable) {
        onlyTalentPool = this.candidateInfo.onlyTalentPool
        if (onlyTalentPool !== Boolean(onlyTalentPool)) {
          onlyTalentPool = true
        }
      }
      const isUpload = this.candidateInfo.isUpload
      const stepData = {
        onlyTalentPool: onlyTalentPool,
        companyJob: baseInfo.companyJob,
        emailAddress: baseInfo.emailAddress,
        firstName: baseInfo.firstName,
        lastName: baseInfo.lastName,
        salutation: baseInfo.salutation,
        gender: baseInfo.gender,
        birthday: baseInfo.birthday,
        photo: baseInfo.photo,
        country: baseInfo.country,
        province: baseInfo.province,
        city: baseInfo.city,
        currentCountry: baseInfo.currentCountry,
        currentProvince: baseInfo.currentProvince,
        currentCity: baseInfo.currentCity,
        address: baseInfo.address,
        otherAddress: baseInfo.otherAddress,
        mobile: baseInfo.mobile,
        secondPhone: baseInfo.secondPhone,
        idNumber: baseInfo.idNumber,
        chatTool: chatToolList,
        socialNetwork: socialNetworkList,
        personalStatement: baseInfo.personalStatement,
        currency: baseInfo.currency,
        salary: baseInfo.salary,
        resumeFileType: baseInfo.resume_file_type,
        resumeFileUrl: baseInfo.resume_file_url,
        mobileCountryCode: baseInfo.mobileCountryCode
      }
      // 如果是HR 或者 Agency 角色，并且是上传的简历，那么只能在有可以编辑的情况下替换邮箱或手机号码
      if ((this.isHr || this.isAgency) && isUpload) {
        stepData.emailAddress = this.isEmailEditable ? stepData.emailAddress : this.stepData.emailAddress
        stepData.mobile = this.isMobileEditable ? stepData.mobile : this.stepData.mobile
      }
      // 求职者编辑简历的时候，邮箱和手机号码是不可以通过常规的方式修改，只能通过特定的方式修改
      // 判断逻辑移动至 layout resetCandidateInfo
      // if (this.isCandidate) {
      //   stepData.emailAddress = this.stepData.emailAddress || stepData.emailAddress
      //   stepData.mobile = isUpload ? this.stepData.mobile : this.stepData.mobile || stepData.mobile
      //   stepData.mobileCountryCode = isUpload ? this.stepData.mobileCountryCode : this.stepData.mobileCountryCode || baseInfo.mobileCountryCode
      // }
      this.photoFile = baseInfo.photoFile
      return stepData
    },
    getPhotoPath (photo) {
      if (!process.env.production) {
        if (photo && !photo.startsWith('blob')) {
          return this.$DjangoBaseUrl(photo)
        } else {
          return photo
        }
      }
      return photo
    },
    handleImport () {
      this.$emit('handleImportResumeClick')
    },
    handleModifyMobile () {
      this.dialogTableVisible = true
    },
    updateMobileSuccess (mobileInfo) {
      this.stepData.mobile = mobileInfo.phone
      this.stepData.mobileCountryCode = mobileInfo.countryCode
    }
  },
  watch: {
    '$i18n.locale' () {
      this.formErrors = {
        email: {
          checked: null,
          error: null
        },
        phone: {
          checked: null,
          error: null
        },
        secondPhone: {
          checked: null,
          error: null
        },
        companyJob: {
          checked: null,
          error: null
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.candidate-base-info-container {
  position: relative;

  .resume-info-head {
    text-align: right;

    .apply-link {
      margin-right: 20px;
    }
  }

  .switch-mini-resume {
    text-align: right;
    margin-bottom: 24px;

    .switch-button {
      font-size: 14px;
      font-weight: 400;
    }

    .active {
      border-color: #0096cc;
      color: #0096cc;
    }
  }

  .candidate-base-info {
    .candidate-location-editor {
      line-height: normal;

      ::v-deep {
        .table-list-wrapper {
          .filter-group {
            .el-input {
              .el-input__inner {
                border-color: #b2b2b2;

                &:focus {
                  border-color: #b2b2b2;
                }

                &:hover {
                  border-color: #b2b2b2;
                }
              }
            }
          }
        }

        .location-editor-container {
          .location-info {
            .el-input {
              .el-input__inner {
                border-color: #b2b2b2;

                &:focus {
                  border-color: #b2b2b2;
                }

                &:hover {
                  border-color: #b2b2b2;
                }
              }
            }
          }
        }
      }
    }

    .span-info {
      color: #606266;
    }

    ::v-deep {
      .el-form-item__content {
        display: flex;
        align-content: flex-start;
        flex-flow: row wrap;
      }

      .only-talent-pool {
        .el-form-item__content {
          display: block;
          align-content: unset;
          flex-flow: unset;
        }
      }

      .multiple-line.home-form-line {
        .multiple-line-item.county-select {
          margin-right: 12px;
          flex: 1;
        }
        .multiple-line-item.province-select {
          margin-right: 12px;
          flex: 1;
        }
        .multiple-line-item.city-select {
          flex: 1;
        }
      }

      .tool-list-container {
        width: 100%;

        .tool-list-item {
          display: flex;
          justify-content: space-between;
          align-items: center;

          + .tool-list-item {
            margin-top: 10px;
          }

          .tool-type {
            flex: 1;

            &:hover, &:focus {
              ~ .tool-account {
                margin-left: 0px;
              }
            }
          }

          .tool-account {
            flex: 1;
            margin-left: -1px;
          }

          .delete-tool-btn {
            margin-left: 10px;
            font-size: 20px;
            cursor: pointer;
          }
        }

        .add-tool-btn {
          padding: 0;
          border: none;
        }
      }
    }

    .photo-uploader {
      .photo-container {
        position: relative;
        overflow: hidden;

        &:hover, &.is-empty {
          .photo-selector {
            transform: translateY(0);
          }
        }

        &.is-empty {
          .photo {
            visibility: hidden;
          }
        }

        .photo {
          display: block;
          width: 100px;
          height: 100px;
        }

        .photo-selector {
          display: flex;
          width: 100%;
          height: 100%;
          background: rgba($color: #000000, $alpha: 0.3);
          transition: transform .3s;
          transform: translateY(-100%);
          position: absolute;
          top: 0;
          left: 0;

          .selector-btn {
            margin: auto;
            text-align: center;
            cursor: pointer;
            color: #ffffff;
          }
        }
      }

      .file-selector {
        display: none;
      }
    }

    .mobile-container {
      width: 100%;
      .mobile-input {
        width: 50%;
        margin-right: 5%;
      }

      .update-mobile {
        padding: 0;
        border: none;
      }
    }
  }

  ::v-deep {
    .update-mobile-container.simple-form.el-form {
      .submit-btn {
        font-size: 14px;
      }

      .el-form-item__label {
        font-size: 14px;
      }

      .el-form-item__content {
        font-size: 14px;
      }

      .el-input {
        font-size: 14px;
      }
    }
  }

  .duplicate-talent-candidate-dialog {
    .talent-link {
      font-size: 14px;
      display: flex;
      align-items: center;
      margin-bottom: 10px;
      .tip {
        white-space: pre;
      }
      ::v-deep {
        .el-link.el-link--default {
          font-size: 14px;
          color: #0096cc;
          .el-link--inner {
            font-size: 14px;
          }
        }
      }
    }
  }

  .loading {
    position: absolute;
  }
}

.candidate-base-info ::v-deep {
  .email-form-line,
  .phone-form-line,
  .second-phone-form-line {
    &.is-error {
      margin-bottom: 0;

      .el-form-item__error {
        min-height: 22px;
        position: initial;
      }
    }
  }
}

.has-resume-view {
  .resume-info-head {}

  .switch-mini-resume {
    width: 80%;
    .switch-button {
    }
  }

  .candidate-base-info {
    width: 80%;
    .candidate-location-editor {}

    .span-info {}

    .photo-uploader {
      .photo-container {
        .photo {
          width: 64px;
          height: 64px;
        }
        .photo-selector {
          .selector-btn {
            font-size: 12px;
          }
        }
      }
    }

    .mobile-container {}

    ::v-deep {
      .el-form-item__content {}

      .only-talent-pool {}

      .multiple-line.home-form-line {
        .multiple-line-item.county-select {
          margin-right: unset;
        }
        .multiple-line-item.province-select {
          margin-right: unset;
        }
        .multiple-line-item.city-select {
        }
      }

      .el-form-item__label {
        font-size: 12px;
        line-height: 26px !important;
        min-height: 26px !important;
      }

      .el-input__inner {
        font-size: 12px;
        height: 26px !important;
      }

      .el-form-item__content {
        line-height: 26px !important;
      }

      .el-textarea__inner {
        font-size: 12px;
      }

      .el-select-dropdown__item {
        font-size: 12px !important;
      }
    }
  }

  ::v-deep {
    span {
      font-size: 12px;
    }
  }
}
</style>
